import * as React from "react";
import { RenderEngine } from "./Controller/3DController/core/RenderEngine";

import Menu from './Menu/Menu';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles';

import { IMenuProps } from './Menu/MenuProps';
import { RequestService, ICheckoutArgument } from "./Service//RequestService";
import { FINGER_SIZES, IFingerSizeFormat, FINGER_SIZE_FORMATS } from "./Model/RingSizes";
import { IRingData, IMetalGroup, IStoneGroup, ISavedRingConfiguration, IAppConfig } from "./sharedInterfaces";
import { IMetalGroupExt, RingModel, IStoneGroupExt } from "./Model/RingModel";
import { MainController } from "./Controller/AppController/MainController";
import { RingStateController, IRingState, IUpdateMetalGroupOptions } from "./Controller/RingStateController/RingStateController";
import { LIMITED_MATERIALS } from "./Model/RingMaterials";
import { Tool, EToolType } from "./Tools/Tools";
import { ThreeDModel } from "./ThreeDModel";
import { isMobile } from "./Menu/MenuIFR/ui-helpers";
import { ConfiguratorContainer } from "./styled";
import { IMediaQueryConstants } from "./styles/theme";
import { ThreeDModelContainer } from "./ThreeDModel/ThreeDModel-styled";
import { getRandomCharacters } from "./Menu/shared/UIHelpers/Functions/Functions";
import { APIController } from "./Controller/APIController/APIController";
import { AssetInfoTypes } from "@viscircle-org/threngine";
import { CustomInputSimple } from "./Controller/SimpleCustomInput";


interface Props {
  appConfig:IAppConfig,
  onInitialized:()=>void,
  mediaQueryConstants:IMediaQueryConstants
}

interface State {
  windowHeight:number,
  modelVisible:boolean,

  renderEngine:RenderEngine,
  requestService:RequestService,
  mainController:MainController,
  apiController:APIController,
  ringStateController:RingStateController,

  rings:RingModel[],
  ringState:IRingState,
  ringScreenshot:string,

  fingerSizeFormat:IFingerSizeFormat,
  autoRatio:boolean,

  price:number,
  checkoutIssues:('fingerSize'|'debug')[],
  checkoutModalVisible:boolean,
  checkoutURI:string,
  shortLinkModalVisible:boolean
  shortLink:string,
  summaryModalVisible:boolean,
  configID:string

  currentMenuTab:number,
  currentMenuMetalGroup:IMetalGroup,  //FIXME: only track currentMenuMetalGroup.name:String?
  currentMenuStoneGroup:IStoneGroup,

  savedCameraDistance:number,
  modelFullscreen:boolean,

  activeTool:EToolType,
  toolProps:any
}

interface IOnStoneGroupChangeOptions {
  forceUpdate?:boolean,
}

export class App extends React.Component<Props,State> {
  private _VERSION_STRING:string = "VCAPPVERSION >= 230120";
  private _DEBUG_LOCAL_RING_DATA:string = null;
 

  constructor(props:Props){
    super(props);

    const { appConfig } = props;

    const renderEngine = new RenderEngine();
    const requestService = new RequestService();
    const mainController = new MainController({
      renderEngine, 
      requestService, 
      assetURI: appConfig.assetURI, 
      logoStyle: appConfig.logoStyle,
      getRingState: () => this.state.ringState,
      saveRingConfiguration: this.saveRingConfiguration,
      onUpdate: () => this.setState(this.state)
    });
    const apiController = new APIController(this);

    this.state = {
      windowHeight: window.innerHeight,
      modelVisible: false,

      renderEngine: renderEngine,
      requestService: requestService,
      mainController: mainController,
      apiController: apiController,
      ringStateController: null,

      rings: [],
      ringState: null,
      ringScreenshot: null,

      fingerSizeFormat: FINGER_SIZE_FORMATS.find(rsf => rsf.id === 'auuk'),
      autoRatio: false,

      price: 0,
      checkoutIssues: [],
      checkoutModalVisible: false,
      checkoutURI: null,
      shortLinkModalVisible: false,
      shortLink: null,
      summaryModalVisible: false,
      configID: null,

      currentMenuTab: -1,
      currentMenuMetalGroup: null,
      currentMenuStoneGroup: null,

      savedCameraDistance: 0,
      modelFullscreen: false,

      activeTool: EToolType.None,
      toolProps: null
    }
  }

  /*
  / GENERAL STUFF
  */

  private _handleFonts = () => {
    const assetURI = this.props.appConfig.assetURI;

    const fonts = [
      new FontFace('Maison Neue', `url(${assetURI}/fonts/MaisonNeue-Black.otf) format('opentype')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Maison Neue', `url(${assetURI}/fonts/MaisonNeue-Bold.otf) format('opentype')`, { weight: 'bold', style: 'normal' }),
      new FontFace('Dakota', `url(${assetURI}/fonts/Dakota-Inf-wf.woff) format('woff')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Snell Roundhand', `url(${assetURI}/fonts/SnellRoundhand-Inf-wf.woff) format('woff')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Brush Script', `url(${assetURI}/fonts/BrushScriptStdInf.woff) format('woff')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Century Gothic', `url(${assetURI}/fonts/CenturyGothicInf.woff) format('woff')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Trajan', `url(${assetURI}/fonts/TrajanProRegularInf.woff) format('woff')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Soehne Breit', `url(${assetURI}/fonts/soehne-breit-web-buch.ttf) format('truetype')`, { weight: 'normal', style: 'normal' }),
      new FontFace('Soehne Breit', `url(${assetURI}/fonts/soehne-breit-web-dreiviertelfett.ttf) format('truetype')`, { weight: 'bold', style: 'normal' })
    ];

    fonts.forEach((font) => {
      font.load().then((loadedFont) => {
        document.fonts.add(loadedFont);
      })
    });
  };

  private _getInitialRingState = (ringModel:RingModel, ringData:IRingData, loadedConfig:ISavedRingConfiguration):IRingState => {

    const loadMetalGroupState = (metalGroup:IMetalGroupExt):IMetalGroupExt => {
      const loadedMetalGroup = loadedConfig.metalGroups.find(lmg => lmg.name === metalGroup.name);
      metalGroup.material = loadedMetalGroup.material;
      metalGroup.purity = loadedMetalGroup.purity;
      metalGroup.surface = loadedMetalGroup.surface;
      return metalGroup;
    };// /const loadMetalGroupState()

    const loadStoneGroupState = (stoneGroup:IStoneGroupExt):IStoneGroupExt => {
      const loadedStoneGroup = loadedConfig.stoneGroups.find(lsg => lsg.name === stoneGroup.name);
      stoneGroup.stone = loadedStoneGroup.stone;
      return stoneGroup;
    };// /const loadStoneGroupState()

    const initialRingState:IRingState = {
      fingerSize: '--',
      ringModel: ringModel,
      width: ringData.defaultWidth, 
      thickness: ringData.defaultThickness,
      stoneSize: ringData.defaultStoneSize ? ringData.defaultStoneSize : 2,
      metalGroups: ringModel.metalGroups,
      metalZones: ringModel.metalZones,
      stoneGroups: ringModel.stoneGroups,
      stoneZones: ringModel.stoneZones,
      engravingText: '',
      engravingFont: 'Brush Script'
    };

    if (loadedConfig) {
      initialRingState.fingerSize = loadedConfig.fingerSize;
      initialRingState.width = loadedConfig.width;
      initialRingState.thickness = loadedConfig.thickness;
      initialRingState.stoneSize = loadedConfig.stoneSize;
      initialRingState.metalGroups = initialRingState.metalGroups.map(mg => loadMetalGroupState(mg));
      initialRingState.stoneGroups = initialRingState.stoneGroups.map(sg => loadStoneGroupState(sg));
      initialRingState.engravingText = loadedConfig.engravingText;
      initialRingState.engravingFont = loadedConfig.engravingFont;

      this.setState({
        fingerSizeFormat: FINGER_SIZE_FORMATS.find(fsf => fsf.id === loadedConfig.fingerSizeFormat)
      });
    };

    return initialRingState;
  };// /_getInitialRingState()

  public componentDidMount = async () => {
    //<DEV>
    if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
      (window as any).app = this;
    }
    //</DEV>

    // console.log(this._VERSION_STRING);

    this._handleFonts();

    //UI stuff
    window.addEventListener('resize', () => {
      this.setState({
        windowHeight: window.innerHeight
      });
    });

    // console.log(this.props.mediaQueryConstants);
    theme.mediaQueryConstants.desktopSmall = this.props.mediaQueryConstants.desktopSmall;
    theme.mediaQueryConstants.phone = this.props.mediaQueryConstants.phone;
    theme.mediaQueryConstants.phoneTiny = this.props.mediaQueryConstants.phoneTiny;
    theme.mediaQueryConstants.veryLowHeight = this.props.mediaQueryConstants.veryLowHeight;
    //---

    await this.state.mainController.initialize();
    this.state.apiController.initialize();

    this.state.requestService.setOsURI(this.props.appConfig.osURI);
    this.state.requestService.setPriceCheckURI(this.props.appConfig.priceCheckURI);
    this.state.requestService.setCostPriceCheckURI(this.props.appConfig.costPriceCheckURI);
    this.state.requestService.setBackendURI(this.props.appConfig.backendURI);

    const getRingDataResponse = await this._getRingData();
    const { dataForAllRings, dataForInitialRing } = getRingDataResponse;

    // console.log("dataForInitialRing", dataForInitialRing);

    const rings:RingModel[] = [];

    const initialRing = new RingModel(dataForInitialRing.ringData);
    rings.push(initialRing);

    dataForInitialRing.ringData.relatedRings.forEach((rr) => {
      console.log(rr, dataForAllRings);

      const relatedRingData = dataForAllRings.find((ring) => ring.SKU === rr.SKU);

      if (relatedRingData) {
        //Sometimes rings are listed as one of their own related rings, in that case we want to ignore them
        if (relatedRingData.SKU !== initialRing.SKU) {
          const relatedRing = new RingModel(relatedRingData);
          rings.push(relatedRing);
        }
      } else {
        console.warn('Related ring not found', rr);
      };
    });

    rings.sort((a, b) => a.stoneCount - b.stoneCount);
    
    this._initializeConfigurator(rings, initialRing, dataForInitialRing);
  };// /componentDidMount()

  private _initializeConfigurator = async (rings:RingModel[], initialRing:RingModel, dataForInitialRing:{ringData:IRingData, loadedConfig:ISavedRingConfiguration}):Promise<void> => {
    const { appConfig } = this.props;
    const { mainController } = this.state;
    const initialRingState = this._getInitialRingState(initialRing, dataForInitialRing.ringData, dataForInitialRing.loadedConfig);
    const ringStateController = new RingStateController(this.state.renderEngine, initialRingState);

    // console.log("initialRingState", initialRingState);
    // console.log("rings", rings);

    this.setState({
      rings: rings,
      ringStateController: ringStateController,
      ringState: initialRingState
    }, async () => {
      const mount = document.querySelector('.threed-configurator-model') as HTMLDivElement;

      await this.state.renderEngine.initEngine(mount, rings);

      const promises = rings.map(r => r.initialize(this.state.renderEngine));
      await Promise.all(promises);
      await this.state.ringStateController.switchRing(initialRing);

      //update logo/ engraving after ring meshes are loaded and morphOptions applied
      this._setRingWidth(initialRingState.width);
      this._setRingThickness(initialRingState.thickness);
      this._setRingStoneSize(initialRingState.stoneSize);
      this._changeMenuMetalGroup(initialRingState.metalGroups[0] ? initialRingState.metalGroups[0].name : null);
      this._changeMenuStoneGroup(initialRingState.stoneGroups[0] ? initialRingState.stoneGroups[0].name : null);

      this.state.renderEngine.autoRotationDelay = appConfig.autoRotationDelay;
      this.state.renderEngine.autoRotationSpeed = appConfig.autoRotationSpeed;

      if (!appConfig.specialMode) this.state.renderEngine.initializeAutoRotation();

      const newState:Partial<State> = {};

      if (appConfig.menuStyle === 'luxe') {
        this.state.renderEngine.setCameraDistance(0.001);
        this.state.renderEngine.setBackground(null);
        this.state.renderEngine.reflectionVisible = false;
        if (!dataForInitialRing.loadedConfig) this._setFingerSizeFormat("USA & Canada");
      }
      else if(appConfig.menuStyle === 'simple'  || appConfig.menuStyle == 'simpleLuxe'){
        this.noEngraving = true;
        this.state.renderEngine.engine.services.render.cameraController.setInputController(this.state.renderEngine.customInput)
        this.state.renderEngine.customInput.zoomSource.unsubscribeAll();
        this.state.renderEngine.customInput.zoomSourceTwo.unsubscribeAll();
        this.state.renderEngine.setCameraDistance(0.16);
        this.state.renderEngine.setBackground(null);
        //this.state.renderEngine.autoRotationSpeed = 0;
        this.state.renderEngine.reflectionVisible = false;

        if (!dataForInitialRing.loadedConfig) this._setFingerSizeFormat("Australia & UK");
        if(isMobile()){
          let camera = this.state.renderEngine.engine.services.render.cameraController.cameraProperties;
          camera.distance_min = 0.001;
          camera.distance_max = 1
          //console.log(camera.distance_min + ":" + camera.distance_max)
          
          this.state.renderEngine.engine.services.render.cameraController.setOrbitCamera(camera);
          this.state.renderEngine.setCameraDistance(0.12);
        }
      }

      if (appConfig.specialMode === 'material-image-capture') {
        newState.activeTool = EToolType.MaterialImageCapturer;
      };

      if (appConfig.specialMode === 'capture') {
        await mainController.initializeCaptureController(appConfig.captureConfig);
        newState.activeTool = EToolType.Capture;
        newState.toolProps = { captureController: mainController.captureController };
      };

      this.setState(newState as State, () => {
        window.setTimeout(() => {
          this.setState({
            modelVisible: true
          }, () => {
            this.props.onInitialized();
          })
        }, 100);
      })
    });
  }

  private _fetchRingData = async (SKU?:string):Promise<IRingData[]> => {
    const { appConfig: config } = this.props;
    const { requestService } = this.state;
    const querySKU = SKU ? [SKU] : null;

    /* Edge cases */
    if (config.specialMode  === 'material-image-capture') {
      console.warn("--- MATERIAL IMAGE CAPTURE ---");
      const res = await fetch('./local/IMAGECAPTURE.json');
      return res.json();
    };

    if (this._DEBUG_LOCAL_RING_DATA) {
      console.warn(`Using local ring data: ${this._DEBUG_LOCAL_RING_DATA}.json`);
      const res = await fetch(`./local/${this._DEBUG_LOCAL_RING_DATA}.json`);
      return res.json();
    };

    /* Normal use */
    const ringData:IRingData[] = await requestService.fetchRingData(querySKU);
    
    /* If no SKU was specified we will have ringData for all rings at this point, meaning we're done. */
    if (ringData.length > 1) return ringData;

    /* Otherwise we will only have ringData for a single ring and need to add ringData for its relatedRings */
    const dataForInitialRing:IRingData = ringData[0];

    const relatedRings = [];
    dataForInitialRing.relatedRings.forEach((relatedRing) => {
      //Sometimes rings are listed as one of their own related rings, in that case we want to ignore them. Sometimes there's an artifact (SKU = "") in related rings, we want to ignore this.
      if (relatedRing.SKU !== dataForInitialRing.SKU && relatedRing.SKU.length > 0) relatedRings.push(relatedRing.SKU);
    })

    if (relatedRings.length > 0) {
      const res = await requestService.fetchRingData(relatedRings);
      res.forEach((relatedRingData) => {
        ringData.push(relatedRingData)
      })
    }

    return ringData;
  }

  private _getRingData = async ():Promise<{ dataForAllRings:IRingData[], dataForInitialRing:{ringData:IRingData, loadedConfig:ISavedRingConfiguration} }> => {
    const { appConfig } = this.props;

    //FIXME: DEBUG STUFF! REMOVE!
    // this._DEBUG_LOCAL_RING_DATA = "IN6002LOCAL";

    let dataForAllRings:IRingData[] = [];
    let dataForInitialRing = { ringData: null, loadedConfig: null };

    /* Loading a saved ring configuration */
    if (appConfig.savedRingConfiguration) { 
      console.log("Loading saved ringConfiguration!", appConfig.savedRingConfiguration);
      if (typeof appConfig.savedRingConfiguration === "string") {
        dataForInitialRing.loadedConfig = await this._loadRingConfiguration(appConfig.savedRingConfiguration);
      } else {
        dataForInitialRing.loadedConfig = appConfig.savedRingConfiguration;
      }

      dataForAllRings = await this._fetchRingData(dataForInitialRing.loadedConfig.SKU);
      
      dataForInitialRing.ringData = dataForAllRings.find((el) => el.SKU === dataForInitialRing.loadedConfig.SKU);

      return ({ dataForAllRings, dataForInitialRing });
    };

    /* Loading a specific ring */
    if (appConfig.initialRingSKU) {
      dataForAllRings = await this._fetchRingData(appConfig.initialRingSKU);

      dataForInitialRing.ringData = dataForAllRings.find((el) => el.SKU === appConfig.initialRingSKU);
      if(!dataForInitialRing.ringData) console.error('Failed to load ring data! Please make sure the specified ring exists.', appConfig.initialRingSKU);
      
      return ({ dataForAllRings, dataForInitialRing });
    }

    /* No initial ring specified => just load the first one in the list of rings */
    if (!appConfig.initialRingSKU) {
      dataForAllRings = await this._fetchRingData(appConfig.initialRingSKU);

      if (!dataForAllRings || dataForAllRings.length < 1) throw new Error("NO RINGS FOUND!");
      dataForInitialRing.ringData = dataForAllRings[0]

      return ({ dataForAllRings, dataForInitialRing });
    }
  };

  public getCheckoutArgument = ():ICheckoutArgument => {
    const { ringState } = this.state;
    const fingerSize = FINGER_SIZES.find(rs => rs[this.state.fingerSizeFormat.id] === ringState.fingerSize);

    const checkoutArgument:ICheckoutArgument = {
      SKU: ringState.ringModel.SKU,
      fingerSize: fingerSize ? fingerSize.auuk : '--',
      width: ringState.width,
      thickness: ringState.thickness,
      metalGroups: ringState.metalGroups.map((mg) => {
        return {
          name: mg.name,
          material: mg.material,
          surface: mg.surface,
          purity: mg.purity
        }
      }),
      metalZones: ringState.metalZones.map((mz) => {
        return {
          id: mz.id,
          material: mz.material,
          surface: mz.surface,
          purity: mz.purity
        }
      }),
      stoneGroups: ringState.stoneGroups.map((sg) => {
        return {
          name: sg.name,
          stone: sg.stone,
          stoneSize: ringState.stoneSize
        }
      }),
      engravingText: ringState.engravingText,
      engravingFont: ringState.engravingFont,
      thumbnail: null,
      additionalData: this.props.appConfig.additionalCheckoutData
    }

    return checkoutArgument;
  }

  public updatePrice = async (checkoutArgument:ICheckoutArgument):Promise<void> => {
    //<DEV>
    // if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
    //   this.setState({
    //     price: 0
    //   })
    //   return;
    // }
    //</DEV>

    let price = await this.state.requestService.getPrice(checkoutArgument)
    if (!price) {
      console.error("Price received from OS was null!");
      price = 0;
    };

    this.setState({
      price: price
    })

    return;
  }

  public checkOut = async ():Promise<void> => {
    const { apiController } = this.state;
    const { appConfig } = this.props;
    const checkoutIssues = [];
    const checkoutArgument:ICheckoutArgument = this.getCheckoutArgument();

    if (!checkoutArgument.fingerSize || checkoutArgument.fingerSize === '--') {
      checkoutIssues.push('fingerSize');

      if (appConfig.menuStyle === "ifr") {
        this._changeMenuTab(0); //Dimensions tab is always the first tab in IFR-style menu
      } else if (appConfig.menuStyle === "luxe") {
        //FIXME: TODO: Flash fingerSize dropdown
        this._changeMenuTab(this.state.ringState.metalGroups.filter((mg) => mg.hideFromMenu === false).length) //Dimensions tab is always first after metalGroup-specifc tabs
      }
    }

    if (checkoutIssues.length === 0) {
      if (apiController.windowObject.onOrderButtonClick) return apiController.windowObject.onOrderButtonClick();

      try {
        this._setCheckoutModalVisibility(true);
        const thumbnail = await this.state.renderEngine.getCheckoutScreenshot(false)
        checkoutArgument.thumbnail = thumbnail;

        const checkoutURI = await this.state.requestService.getCheckoutURI(checkoutArgument);

        this.setState({
          checkoutURI: checkoutURI
        }, () => {
          window.setTimeout(() => {
            if (!appConfig.noAutomaticRedirect) {
              window.top.location.href = checkoutURI;
            };
          }, 3000);
        });
      } catch (err) {
        console.error("Error on checkout!", err);
      }

    } else {
      console.log("checkOut() failed!", checkoutIssues);
    };

    this.setState({
      checkoutIssues: checkoutIssues
    })
  }

  private _onShareButtonClick = () => {
    const { requestService, ringStateController } = this.state;
    const ringState = ringStateController.getState();

    this._setShortLinkModalVisibility(true);

    this.saveRingConfiguration()
    .then((configId) => {      
      return requestService.getShortLink(this.props.appConfig.shortLinkTarget.replace('[CONFIG_ID]', configId));
    })
    .then((shortLink) => {
      this.setState({
        shortLink: shortLink
      });
    });
  };

  public saveRingConfiguration = async ():Promise<string> => {
    const { mainController, ringStateController, fingerSizeFormat } = this.state;
    const ringState = ringStateController.getState();

    const configId = await mainController.saveRingConfiguration(ringState, fingerSizeFormat);
    return Promise.resolve(configId);
  };

  private _loadRingConfiguration = (id:string):Promise<ISavedRingConfiguration> => {
    const { requestService } = this.state;

    return new Promise((resolve, reject) => {
      requestService.loadConfiguration(id)
      .then((res) => {
        resolve(res as ISavedRingConfiguration)
      });
    });
  }

  /*
  / RING STATE STUFF
  */

  private _switchRing = (newRing:RingModel):Promise<void> => {
    const { ringStateController, ringState } = this.state;
    const previousMenuMetalGroup = this.state.currentMenuMetalGroup;
    const previousMenuStoneGroup = this.state.currentMenuStoneGroup;
    
    return new Promise((resolve, reject) => {
      ringStateController.switchRing(newRing)
      .then(() => {
        this.setState({
          ringState: ringStateController.getState()
        }, () => {
          const { ringState } = this.state;
          
          //Set currently active metalGroup and stoneGroup for menu
          let newMenuMetalGroup = null;
          let newMenuStoneGroup = null;

          //Default to first group in list of groups
          if (ringState.metalGroups.length > 0) {
            newMenuMetalGroup = ringState.metalGroups[0];
          } else {
            console.warn("New ring appears to have no metalGroups.");
          };

          if (ringState.stoneGroups.length > 0) {
            newMenuStoneGroup = ringState.stoneGroups[0];
          };

          //If we had a previously selected group on the old ring, try to find a group with the same name on the new ring
          if (previousMenuMetalGroup) {
            const match = ringState.metalGroups.find((mg) => mg.name === previousMenuMetalGroup.name);
            if (match) {
              newMenuMetalGroup = match;
            } else {
              console.warn(`Previously selected metalGroup "${previousMenuMetalGroup}" does not exist on current ring. Falling back on default metalGroup.`, newMenuMetalGroup);
            }
          }

          if (previousMenuStoneGroup) {
            const match = ringState.stoneGroups.find((sg) => sg.name === previousMenuStoneGroup.name);
            if (match) {
              newMenuStoneGroup = match;
            } else {
              console.warn(`Previously selected stoneGroup "${previousMenuStoneGroup}" does not exist on current ring. Falling back on default stoneGroup.`, newMenuStoneGroup);
            }
          }

          this.setState({
            currentMenuMetalGroup: newMenuMetalGroup,
            currentMenuStoneGroup: newMenuStoneGroup
          }, () => {
            resolve();
          });
        });
      })
    });
  }

  private _setRingFingerSize = (setTo:string) => {
    const { ringStateController, apiController } = this.state;

    setTo = setTo.replace('¼', '1/4');
    setTo = setTo.replace('½', '1/2');
    setTo = setTo.replace('¾', '3/4');

    ringStateController.setFingerSize(setTo);

    const checkoutIssues = this.state.checkoutIssues.filter((ci) => ci !== 'fingerSize');

    this.setState({
      ringState: ringStateController.getState(),
      checkoutIssues: checkoutIssues
    }, async () => {
      //console.log("_setRingFingerSize", this.state.ringState.fingerSize)
      const checkoutArgument = this.getCheckoutArgument();
      await this.updatePrice(checkoutArgument);
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }

  private _setFingerSizeFormat = (setTo:string) => {
    const { ringStateController } = this.state;
    //default fingerSize
    ringStateController.setFingerSize('R');

    this.setState({
      ringState: ringStateController.getState(),
      fingerSizeFormat: FINGER_SIZE_FORMATS.find(rsf => rsf.name === setTo)
    }, () => {
      //console.log("_setFingerSizeFormat", this.state.fingerSizeFormat)
    });
  }

  private _setRingWidth = async (setTo:number) => {
    const { ringStateController, autoRatio, apiController } = this.state;
    
    //Data model updates instantly. Asynchronous stuff is purely visual, so we don't need to wait for it.
    ringStateController.setWidth(setTo, autoRatio);

    this.setState({
      ringState: ringStateController.getState()
    }, async () => {
      //console.log("_setRingWidth!", this.state.ringState.width);
      this._updateLogo();
      this._updateEngraving();
      const checkoutArgument = this.getCheckoutArgument();
      await this.updatePrice(checkoutArgument);
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }

  private _setRingThickness = (setTo:number) => {
    const { ringStateController, apiController } = this.state;

    ringStateController.setThickness(setTo);

    this.setState({
      ringState: ringStateController.getState()
    }, async () => {
      //console.log("_setRingThickness!", this.state.ringState.thickness);
      const checkoutArgument = this.getCheckoutArgument();
      await this.updatePrice(checkoutArgument);
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }

  private _setRingStoneSize = (setTo:number) => {
    const { ringStateController, apiController} = this.state;

    ringStateController.setStoneSize(setTo);

    this.setState({
      ringState: ringStateController.getState()
    }, async () => {
      // console.log("_setRingStoneSize!", this.state.ringState.stoneSize);
      const checkoutArgument = this.getCheckoutArgument();
      await this.updatePrice(checkoutArgument);
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  };// /_setRingStoneSize()

  private _setAutoRatio = (setTo:boolean) => {
    this.setState({
      autoRatio: setTo
    }, () => {
      //console.log("_setAutoRatio!", this.state.autoRatio)
      const { ringState, autoRatio } = this.state;
      if (autoRatio) this._setRingWidth(ringState.width);
    });
  }

  private _onMetalGroupChange = (newMetalGroupState:IMetalGroup, options:IUpdateMetalGroupOptions = {}) => {
    const { ringStateController, apiController } = this.state;

    console.log('_onMetalGroupChange()', newMetalGroupState, options);

    ringStateController.updateMetalGroup(newMetalGroupState, options);
    ringStateController.updateDisabledOptions();

    this.setState({
      ringState: ringStateController.getState()
    }, async () => {
      const checkoutArgument = this.getCheckoutArgument();
      await this.updatePrice(checkoutArgument);
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }

  private _onStoneGroupChange = (newStoneGroupState:IStoneGroup, options:IOnStoneGroupChangeOptions = {}) => {
    const { ringStateController, apiController } = this.state;

    ringStateController.updateStoneGroup(newStoneGroupState, options);

    this.setState({
      ringState: ringStateController.getState()
    }, async () => {
      const checkoutArgument = this.getCheckoutArgument();
      await this.updatePrice(checkoutArgument);
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }

  private _setRingStoneCount = async (setTo:number) => {
    const { rings, ringState, apiController } = this.state;
    const newRing = rings.find(r => r.stoneCount === Number(setTo));
    
    if (ringState.ringModel.stoneCount === Number(setTo)) return;

    await this._switchRing(newRing);
    
    const checkoutArgument = this.getCheckoutArgument();
    await this.updatePrice(checkoutArgument);
    apiController.onConfigurationChange(this.state.ringState, this.state.price);
  }

  private _updateLogo = () => {
    const { mainController, ringStateController } = this.state;
    const ringState = ringStateController.getState();
    mainController.logoController.updateLogo(ringState.width, ringState.ringModel.morphOptions);
  }


  private noEngraving = false;


  private _updateEngraving = () => {
    if(this.noEngraving){
      return;
    }
    const { mainController, ringStateController } = this.state;
    const ringState = ringStateController.getState();
    mainController.updateEngraving(ringState);
  }

  private _changeEngravingText = (setTo:string) => {
    const { ringStateController, apiController } = this.state;

    ringStateController.setEngravingText(setTo);
    this._updateEngraving();

    this.setState({
      ringState: ringStateController.getState()
    }, () => {
      const checkoutArgument = this.getCheckoutArgument();
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }

  private _changeEngravingFont = (setTo:'Brush Script'|'Century Gothic'|'Snell Roundhand'|'Trajan'|'Dakota') => {
    const { ringStateController, apiController } = this.state;

    ringStateController.setEngravingFont(setTo);
    this._updateEngraving();

    this.setState({
      ringState: ringStateController.getState()
    }, () => {
      const checkoutArgument = this.getCheckoutArgument();
      apiController.onConfigurationChange(this.state.ringState, this.state.price);
    });
  }
  
  private _changeMenuTab = (setTo:number) => {
    const { appConfig } = this.props;
    const { renderEngine, currentMenuTab, savedCameraDistance, ringState, checkoutIssues } = this.state;

    //3D model "zoom" (technically camera distance)
    //Remember current camera distance when opening a tab
    const newSavedCameraDistance = (setTo >= 0 && currentMenuTab < 0) ? this.state.renderEngine.getCameraDistance() : savedCameraDistance;

    //If we are opening a menu tab in the mobile view set camera distance to minimum
    if (isMobile() && setTo >= 0 && currentMenuTab < 0) {
      renderEngine.setCameraDistance(0.001);
    };

    //If we are closing a menu tab in the mobile view set camera distance back to what it was before opening the tab
    if (isMobile() && setTo < 0 && currentMenuTab >= 0) {
      renderEngine.setCameraDistance(newSavedCameraDistance);
    };
    //--

    //In the Luxe menu, if we try to go forward from the dimensions tab and fingerSize is not set, prevent tab switch and highlight the fingerSize dropdown
    if (appConfig.menuStyle === "luxe") {
      const dimensionsTabIndex = ringState.metalGroups.filter((mg) => mg.hideFromMenu === false).length;
      if (currentMenuTab === dimensionsTabIndex && setTo > currentMenuTab && (!ringState.fingerSize || ringState.fingerSize === "--")) {
        setTo = currentMenuTab;
        checkoutIssues.push("fingerSize");
      }
    }
    //--

    this.setState({
      savedCameraDistance: newSavedCameraDistance,
      currentMenuTab: setTo,
      checkoutIssues: checkoutIssues
    }, () => {
      const engine = this.state.renderEngine.engine;
      if (engine && engine.services.render.cameraController) { //FIXME: TryCatch instead of this?
        (engine as any)._onResize();
      }
      this.state.renderEngine.updateZoomSensitivity(isMobile() ? 0.1 : 0.15);
      this.setState(this.state);
    });
  }

  private _changeMenuMetalGroup = (setTo:string) => {
    if (!setTo) return;
    this.setState({
      currentMenuMetalGroup: this.state.ringState.metalGroups.find((mg) => mg.name === setTo)
    }, () => {
      //console.log("_changeMenuMetalGroup!", this.state.currentMenuMetalGroup);
    });
  };// /_changeMenuMetalGroup()

  private _changeMenuStoneGroup = (setTo:string) => {
    if (!setTo) return;
    this.setState({
      currentMenuStoneGroup: this.state.ringState.stoneGroups.find((sg) => sg.name === setTo)
    }, () => {
      //console.log("_changeMenuStoneGroup!", this.state.currentMenuStoneGroup);
    });
  };// /_changeMenuStoneGroup()

  private _setCheckoutModalVisibility = (setTo:boolean) => {
    this.setState({
      checkoutModalVisible: setTo,
    }, () => {
      //Reset checkoutURI when closing modal
      if (!setTo) {
        this.setState({
          checkoutURI: null
        });
      };
    });
  };// /setCheckoutModalVisibility()

  private _setShortLinkModalVisibility = (setTo:boolean) => {
    this.setState({
      shortLinkModalVisible: setTo,
    }, () => {
      //Reset shortLink when closing modal
      if (!setTo) {
        this.setState({
          shortLink: null
        });
      };
    });
  };// /setShortLinkModalVisibility()

  private _setSummaryModalVisibility = (setTo:boolean) => {
    /* 
      IFR wants to covertly list cost prices of rings. When vendor and customer are using the configurator together the vendor needs to be able to check them without the customer noticing.
      To achieve this we insert the cost price and some random characters into the configuration ID and show it in the info popup.
      This functionality is only wanted in specific instances of the configurator - if appConfig.costPriceURI is not set we skip all of this.
    */    
    if (setTo) {
      const promises:Promise<any>[] = [];
      let costPrice = null;
      let ringScreenshot = null;
      let configID = null;

      const getRingScreenshotPromise = ():Promise<void> => {
        return new Promise((resolve, reject) => {
          this.state.renderEngine.getCheckoutScreenshot(true)
          .then((res) => {
            ringScreenshot = res;
            resolve();
          })
        })
      };

      const getCostPricePromise = ():Promise<void> => {
        return new Promise((resolve, reject) => {
          const ring:ICheckoutArgument = this.getCheckoutArgument();
          this.state.requestService.getCostPrice(ring)
          .then((price:number) => {
            if (!price) price = null;
            costPrice = price;
            resolve();
          });
        }) 
      };

      const getConfigIdPromise_REAL = ():Promise<void> => {
        return new Promise((resolve, reject) => {
          this.state.mainController.saveRingConfiguration(this.state.ringStateController.getState(), this.state.fingerSizeFormat)
          .then((res) => {
            configID = res;
            resolve();
          });
        })
      };

      const getConfigIdPromise_FAKE = ():Promise<void> => {
        return new Promise((resolve, reject) => {
          configID = getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789012345678901234567890', 12);
          resolve();
        });
      };

      const hideCostPrice = () => {
        if (!configID) return;

        let costPriceSegment = '';

        if (!costPrice) {
          costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 1);
          costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789012345678901234567890', 6 - costPriceSegment.length);
        } else {
          costPriceSegment += costPrice.toString();
          costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 1);
          costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789012345678901234567890', 6 - costPriceSegment.length);
        }

        configID = `${configID.slice(0,6)}-${costPriceSegment}-${configID.slice(6)}`.toUpperCase();
      };

      promises.push(getRingScreenshotPromise());

      if (this.props.appConfig.costPriceCheckURI) {
        promises.push(getCostPricePromise());
        promises.push(getConfigIdPromise_FAKE());
      };
      // console.log('configID',configID)
      Promise.all(promises)
      .then((res) => {
        hideCostPrice();

        this.setState({
          summaryModalVisible: setTo,
          ringScreenshot: ringScreenshot,
          configID: configID
        });
      });
    } else {
      this.setState({
        summaryModalVisible: setTo,
        configID: null
      });
    }
  }

  private _setModelFullscreen = (value:boolean) => {
    this.setState({
      modelFullscreen: value
    }, () => {
      this.state.renderEngine.onResize();
      this.setState(this.state);
    })
  }

  private _setConfigId = () => {
    const promises:Promise<any>[] = [];   
    let configID = null;   
    let costPrice = null;

    const getCostPricePromise = ():Promise<void> => {
      return new Promise((resolve, reject) => {
        const ring:ICheckoutArgument = this.getCheckoutArgument();
        this.state.requestService.getCostPrice(ring)
        .then((price:number) => {
          if (!price) price = null;
          costPrice = price;
          resolve();
        });
      }) 
    };

    const getConfigIdPromise_FAKE = ():Promise<void> => {
      return new Promise((resolve, reject) => {
        configID = getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789012345678901234567890', 12);
        resolve();
      });
    };

    const hideCostPrice = () => {
      if (!configID) return;

      let costPriceSegment = '';

      if (!costPrice) {
        costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 1);
        costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789012345678901234567890', 6 - costPriceSegment.length);
      } else {
        costPriceSegment += costPrice.toString();
        costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ', 1);
        costPriceSegment += getRandomCharacters('ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789012345678901234567890', 6 - costPriceSegment.length);
      }

      configID = `${configID.slice(0,6)}-${costPriceSegment}-${configID.slice(6)}`.toUpperCase();
    };

    if (this.props.appConfig.costPriceCheckURI) {
      promises.push(getCostPricePromise());
      promises.push(getConfigIdPromise_FAKE());
    };
    console.log('configID',configID)
    Promise.all(promises)
    .then((res) => {   
      hideCostPrice();
      this.setState({
        configID: configID
      });
    });
  }


  render = () => {
    const {
      appConfig
    } = this.props;

    const {
      rings,
      ringState,
      ringScreenshot,
      configID,
      fingerSizeFormat,
      autoRatio,
      price,
      checkoutIssues,
      checkoutModalVisible,
      checkoutURI,
      shortLinkModalVisible,
      shortLink,
      summaryModalVisible,
      currentMenuTab,
      currentMenuStoneGroup,
      activeTool,
      toolProps,
      modelVisible
    } = this.state;

    let {
      currentMenuMetalGroup,
      modelFullscreen
    } = this.state;

    const tool = <Tool
      app={this}
      activeTool={activeTool}
      toolProps={toolProps}
    />;

    const mobile = isMobile();

    if (!ringState || !ringState.ringModel) {
      return (
        <ThemeProvider theme={theme}>
            <ConfiguratorContainer className="threed-configurator-container"
              menuStyle={this.props.appConfig.menuStyle}
              windowHeight={this.state.windowHeight}
            >
              <div className="Model"></div>
              {tool}
            </ConfiguratorContainer>
        </ThemeProvider>
      );
    };

    const morphOptions = ringState.ringModel.morphOptions;
    const maxStoneSize = ringState.ringModel.getMaxStoneSize(ringState.width, ringState.thickness);
    const minMaxThickness = ringState.ringModel.getMinMaxThickness(ringState.width);

    let metalGroups:IMetalGroupExt[] = ringState.metalGroups.filter(rmg => (rmg.hideFromMenu === false));
    let stoneGroups:IStoneGroupExt[] = ringState.stoneGroups.filter(rsg => (rsg.hideFromMenu === false));

    (metalGroups as IMetalGroup[]) = metalGroups.map((rmg) => {
      const metalGroup:IMetalGroup = {
        name: rmg.name,
        material: rmg.material,
        materialOptions: rmg.materialOptions.filter(mo => !rmg.disabledMaterialOptions.includes(mo)),
        surface: rmg.surface,
        surfaceOptions: rmg.surfaceOptions.filter(so => !rmg.disabledSurfaceOptions.includes(so)),
        purity: rmg.purity,
        purityOptions: rmg.purityOptions.filter(po => !rmg.disabledPurityOptions.includes(po))
      }

      return metalGroup;
    });

    if (currentMenuMetalGroup) {
      currentMenuMetalGroup = metalGroups.find((mg) => mg.name === currentMenuMetalGroup.name);
    };

    (stoneGroups as IStoneGroup[]) = stoneGroups.map((rsg) => {
      const stoneGroup:IStoneGroup = {
        name: rsg.name,
        stone: rsg.stone,
        stoneOptions: rsg.stoneOptions
      }

      return stoneGroup;
    });

    const menuProps:IMenuProps = {
      ringSKU: ringState.ringModel.SKU,
      ringScreenshot: ringScreenshot,
      configID: configID,
      assetURI: this.props.appConfig.assetURI,

      setConfigId:this._setConfigId,

      fingerSize: ringState.fingerSize,
      fingerSizeOptions: ringState.ringModel.fingerSizeOptions.filter((fso) => fso[fingerSizeFormat.id] !== null).map((fso) => fso[fingerSizeFormat.id]),
      onFingerSizeChange: this._setRingFingerSize,

      fingerSizeFormat: fingerSizeFormat.name,
      fingerSizeFormatOptions: FINGER_SIZE_FORMATS.map(rsf => rsf.name),
      onFingerSizeFormatChange: this._setFingerSizeFormat,

      ringWidth: ringState.width,
      ringWidthOptions: morphOptions.width.map((rwo) => rwo),
      onRingWidthChange: this._setRingWidth,

      ringThickness: ringState.thickness,
      ringThicknessOptions: morphOptions.thickness.filter((rto) => rto >= minMaxThickness.min).filter((rto) => rto <= minMaxThickness.max).map((rto) => rto),
      onRingThicknessChange: this._setRingThickness,

      autoRatio: autoRatio,
      onAutoRatioChange: this._setAutoRatio,

      price: price,
      checkoutIssues: checkoutIssues,
      checkoutModalVisible: checkoutModalVisible,
      checkoutURI: checkoutURI,
      setCheckoutModalVisibility: this._setCheckoutModalVisibility,
      onCheckout: this.checkOut,

      shortLinkModalVisible: shortLinkModalVisible,
      shortLink: shortLink,
      setShortLinkModalVisibility: this._setShortLinkModalVisibility,

      summaryModalVisible: summaryModalVisible,
      setSummaryModalVisibility: this._setSummaryModalVisibility,

      metalGroups: metalGroups,
      onMetalGroupChange: this._onMetalGroupChange,

      stoneCut: ringState.ringModel.stoneCut.id,

      stoneCount: ringState.ringModel ? ringState.ringModel.stoneCount : 0,
      stoneCountOptions: rings.map(r => r.stoneCount),
      onStoneCountChange: this._setRingStoneCount,

      stoneSize: ringState.stoneSize,
      stoneSizeOptions: morphOptions.stoneSize.filter(sso => sso <= maxStoneSize).map((sso) => sso),
      onStoneSizeChange: this._setRingStoneSize,

      stoneGroups: stoneGroups,
      onStoneGroupChange: this._onStoneGroupChange,

      engravingText: ringState.engravingText,
      onEngravingTextChange: this._changeEngravingText,
      engravingFont: ringState.engravingFont,
      engravingFontOptions: ['Brush Script', 'Century Gothic', 'Snell Roundhand', 'Trajan', 'Dakota'],
      onEngravingFontChange: this._changeEngravingFont,

      limitedMaterials: LIMITED_MATERIALS,
      
      currentMenuTab: currentMenuTab,
      onMenuTabChange: this._changeMenuTab,

      currentMenuMetalGroup: currentMenuMetalGroup,
      onMenuMetalGroupChange: this._changeMenuMetalGroup,

      currentMenuStoneGroup: currentMenuStoneGroup,
      onMenuStoneGroupChange: this._changeMenuStoneGroup,

      onShareButtonClick: this._onShareButtonClick,
      onLoadButtonClick: () => { window.location.href = `${this.props.appConfig.osURI}/collection/infinity` },

      modelFullscreen: modelFullscreen,
      setModelFullscreen: this._setModelFullscreen
    };

    return (
      <ThemeProvider theme={theme}>
        <ConfiguratorContainer className={`threed-configurator-container`}
          menuStyle={this.props.appConfig.menuStyle}
          windowHeight={this.state.windowHeight}
        >
          {
            ((appConfig.menuStyle == "simple" || appConfig.menuStyle == "simpleLuxe") && !mobile) &&  <Menu
              menuType={appConfig.menuStyle}
              menuProps={menuProps}
            />
          } 
          <ThreeDModelContainer className={`threed-configurator-model-container ${appConfig.menuStyle == 'simple' || appConfig.menuStyle == 'simpleLuxe' ? 'simple-threed-configurator-model-container' : ''}`}
            menuStyle={appConfig.menuStyle}
            currentMenuTab={currentMenuTab}
            mobile={mobile}
            fullSize={modelFullscreen || activeTool !== EToolType.None}
            modelVisible={modelVisible}
          >
            <ThreeDModel
              className={`threed-configurator-model ${appConfig.menuStyle == 'simple' || appConfig.menuStyle == 'simpleLuxe'? 'simple-threed-configurator-model' : ''}`}
            />
          </ThreeDModelContainer>
          {
            ((appConfig.menuStyle != "simple" && appConfig.menuStyle != "simpleLuxe") || ((appConfig.menuStyle == "simple" || appConfig.menuStyle == "simpleLuxe") && mobile)) &&  <Menu
              menuType={appConfig.menuStyle}
              menuProps={menuProps}
            />
          }         
          {tool}
        </ConfiguratorContainer>
      </ThemeProvider>
    )
  }
};// /class App